import clsx from 'clsx';
import debounce from 'lodash/debounce';
import React, { useContext, useRef } from 'react';

import { DeckColumnIdContext } from '@/contexts/deck-column-id-context';
import { useScopeUrl } from '@/hooks/use-scope-url';
import { useStatusHoverCardActions } from '@/stores/status-hover-card';
import { isMobile, userTouching } from '@/utils/is-mobile';

const showStatusHoverCard = debounce(
  (
    openStatusHoverCard: (
      ref: React.RefObject<HTMLDivElement | null>,
      statusId: string,
      columnId?: string,
      scopeUrl?: string,
    ) => void,
    ref: React.RefObject<HTMLDivElement | null>,
    statusId: string,
    columnId?: string,
    scopeUrl?: string,
  ) => {
    openStatusHoverCard(ref, statusId, columnId, scopeUrl);
  },
  300,
);

interface IHoverStatusWrapper {
  statusId: string;
  inline: boolean;
  className?: string;
  children: React.ReactNode;
}

/** Makes a status hover card appear when the wrapped element is hovered. */
const HoverStatusWrapper: React.FC<IHoverStatusWrapper> = ({
  statusId,
  children,
  inline = false,
  className,
}) => {
  const { openStatusHoverCard, closeStatusHoverCard } = useStatusHoverCardActions();

  const ref = useRef<HTMLDivElement>(null);
  const columnId = useContext(DeckColumnIdContext) || undefined;
  const scopeUrl = useScopeUrl();
  const Elem: keyof React.JSX.IntrinsicElements = inline ? 'span' : 'div';

  const handleMouseEnter = () => {
    if (!isMobile(window.innerWidth)) {
      showStatusHoverCard(openStatusHoverCard, ref, statusId, columnId, scopeUrl);
    }
  };

  const handleMouseLeave = () => {
    showStatusHoverCard.cancel();
    setTimeout(() => {
      closeStatusHoverCard();
    }, 200);
  };

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (userTouching.matches) {
      event.preventDefault();
      event.stopPropagation();

      openStatusHoverCard(
        ref as React.RefObject<HTMLDivElement | null>,
        statusId,
        columnId,
        scopeUrl,
      );
      return;
    }

    showStatusHoverCard.cancel();
    closeStatusHoverCard(true);
  };

  return (
    <Elem
      ref={ref}
      className={clsx('hover-status-wrapper', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {children}
    </Elem>
  );
};

export { HoverStatusWrapper as default, showStatusHoverCard };
